import { DeleteIcon, DownloadIcon } from '@chakra-ui/icons';
import {
  AbsoluteCenter,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import {
    deleteInstallmentApi,
    deleteRepaymentApi,
    downloadCustomerMonthlyStatementApi,
    getAmortizationApi,
    getCustomerInstallmentsOverviewApi, getTwelveBankAccountsApi,
} from 'api/dealApi';
import { getFacilityFeesApi } from 'api/facilityFeeApi';
import { listAccountingInvoicesApi } from 'api/accountingInternalApi';
import { formatTwelveBankAccountLabelById } from 'helpers/twelveBankAccounts';
import DataTable from 'components/DataTable';
import {Currency, numberFormat} from 'components/Number';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import Card from 'components/card/Card';
import BackLink from 'components/links/BackLink';
import {
  DEAL_TYPE,
  FEE_TYPE,
  INSTALLMENT_TYPE,
  INSTALLMENT_TYPE_META,
  REVENUE_RECOGNITION_TYPE,
} from 'constants/dealConstants';
import { CockpitCustomerContext } from 'context/CockpitCustomerContext';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { FaSync, FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { readBlobErrorMessage } from 'utils/blobErrorMessage';
import AmortizationTable from './AmortizationTable';
import InstallmentStatusTag from './InstallmentStatusTag';
import InstallmentTypeTag from './InstallmentTypeTag';
import OperationsKpis, { ASSET_TAPE_VIEW } from './OperationsKpis';
import RevenueRecognitionSelect from './RevenueRecognitionSelect';
import CollapsableDataTable from "../../../components/CollapsableDataTable";
import {CSVLink} from "react-csv";
import {INSTALLMENT_STATUS as INSTALLMENT_STATUS} from "../../../constants/installmentConstants";

const columnHelper = createColumnHelper();

// FACILITY_FEE_NESTED_ROW marks a synthetic nested row as a facility fee paid
// alongside the repayment, as opposed to an actual repayment_allocation.
const FACILITY_FEE_NESTED_ROW = '__facilityFee';

const renderInstallmentAllocationRow = (dataRow) => [
    (<>
        <Text whiteSpace={'nowrap'}>
            Installment Date
        </Text>
        <Text fontWeight="bold">
            {moment(dataRow.installment.info.transferDate).format('MMM DD, YYYY')}
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Created At
        </Text>
        <Text fontWeight="bold">
            {moment(dataRow.installment.created_at).format('MMM DD, YYYY')}
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Purchase Price
        </Text>
        <Text fontWeight="bold">
            <Currency value={dataRow.installment.info.purchasePrices[0].amount} maximumFractionDigits={2} />
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Cohort Dates
        </Text>
        <Text fontWeight="bold">
            {dataRow.installment.info.cohortStartDate ? moment(dataRow.installment.info.cohortStartDate).format('MMM DD, YYYY') : '---'} - {dataRow.installment.info.cohortEndDate ? moment(dataRow.installment.info.cohortEndDate).format('MMM DD, YYYY') : '---'}
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Owner
        </Text>
        <Text fontWeight="bold">
            {dataRow.installment.info.owner}
        </Text>
    </>),
    (<>
      <Text whiteSpace={'nowrap'}>
          Repayment Amount
      </Text>
      <Text fontWeight="bold">
          <Currency value={dataRow.amount} maximumFractionDigits={2} />
      </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Principal
        </Text>
        <Text fontWeight="bold">
            <Currency value={dataRow.principal??0} maximumFractionDigits={2} />
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Interest
        </Text>
        <Text fontWeight="bold">
            <Currency value={dataRow.interest??0} maximumFractionDigits={2} />
        </Text>
    </>),
];

const renderFacilityFeeNestedRow = (dataRow) => [
    (<>
        <Text whiteSpace={'nowrap'}>
            Type
        </Text>
        <Text fontWeight="bold">
            Facility Fee
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Due Date
        </Text>
        <Text fontWeight="bold">
            {dataRow.fee.due_date ? moment(dataRow.fee.due_date).format('MMM DD, YYYY') : '---'}
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Transfer Date
        </Text>
        <Text fontWeight="bold">
            {dataRow.fee.transfer_date ? moment(dataRow.fee.transfer_date).format('MMM DD, YYYY') : '---'}
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Amount
        </Text>
        <Text fontWeight="bold">
            <Currency value={dataRow.fee.amount} maximumFractionDigits={2} />
        </Text>
    </>),
    (<>
        <Text whiteSpace={'nowrap'}>
            Comment
        </Text>
        <Text fontWeight="bold">
            {dataRow.fee.comment || '---'}
        </Text>
    </>),
];

const getCurrencyCellValue = (info) => {
  const value = info.getValue();
  return value === null || value === undefined ? (
    'N/A'
  ) : (
    <Currency value={value} maximumFractionDigits={2} />
  );
};

const getCurrencyCellValueNullAsZero = (info) => {
    let value = info.getValue();
    if (value === null || value === undefined) {
        value = 0
    }
    return (
        <Currency value={value} maximumFractionDigits={2} />
    );
};

// A fee paid alongside a repayment (since the repayment_id link was added)
// carries a direct reference to it. A fee paid before that — or matched here
// against older repayment rows — falls back to the shared transfer/paid date
// both were submitted with on the Repayment form, but only among fees with no
// repayment link at all, so a fee already linked to a *different* repayment
// never attaches here just because the dates happen to coincide.
const paidFacilityFeesForRepayment = (facilityFees, repayment) => {
    const linked = (facilityFees || []).filter(
        (fee) => fee.status === 'paid' && fee.repayment_id === repayment.id
    );
    if (linked.length > 0) return linked;

    if (!repayment.paid_at) return [];
    return (facilityFees || []).filter(
        (fee) => fee.status === 'paid' && !fee.repayment_id && fee.transfer_date === repayment.paid_at
    );
};

const paidFacilityFeeAmountForRepayment = (facilityFees, repayment) =>
    paidFacilityFeesForRepayment(facilityFees, repayment).reduce((sum, fee) => sum + (fee.amount || 0), 0);

// A repayment is linked to at most one invoice — the one it was created
// against on the Repayment screen — while an invoice can list many repayments.
const invoiceForRepayment = (invoices, repayment) =>
    (invoices || []).find((invoice) => (invoice.repaymentIds || []).includes(repayment.id));

const sharedInstallmentMetaColumns = () => [
    columnHelper.accessor('transferDate', {
      cell: (info) =>
        info.getValue() === null ? (
          'N/A'
        ) : (
          <Text whiteSpace={'nowrap'}>
            {moment(info.getValue()).format('MMM DD, YYYY')}
          </Text>
        ),
      header: 'Transfer Date',
    }),
    columnHelper.accessor('installmentType', {
      cell: (info) => {
        const t = info.getValue();
        if (!t) {
          return 'N/A';
        }
        return <InstallmentTypeTag type={t} />;
      },
      header: 'Installment Type',
    }),
    columnHelper.display({
      id: 'cohortDates',
      cell: (info) => {
        const { cohortStartDate, cohortEndDate } = info.row.original;
        if (!cohortStartDate || !cohortEndDate) {
          return 'N/A';
        }

        return (
          <Text whiteSpace={'nowrap'}>
            {moment(cohortStartDate).format('MM/DD/YYYY')} -{' '}
            {moment(cohortEndDate).format('MM/DD/YYYY')}
          </Text>
        );
      },
      header: 'Cohort Dates',
    }),
    columnHelper.accessor('status', {
      cell: (info) => {
        const status = info.getValue();
        if (!status) {
          return 'N/A';
        }

        return <InstallmentStatusTag status={status} />;
      },
      header: 'Status',
    }),
];

const installmentActionsColumn = (deleteInstallment) =>
  columnHelper.display({
      id: 'actions',
      meta: {
        maxWidth: 10,
        align: 'right',
      },
      cell: (info) => {
        const { id, installmentType, status } = info.row.original;

        if ((installmentType === INSTALLMENT_TYPE.PREVIEW) || (installmentType === INSTALLMENT_TYPE.FUNDED && status === INSTALLMENT_STATUS.PENDING)) {
          return (
            <IconButton
              aria-label="delete"
              icon={<DeleteIcon color={'red'} />}
              pr={5}
              _hover={{ bg: 'transparent' }}
              bg={'transparent'}
              onClick={(event) => {
                if(window.confirm('Are you sure you want to remove this installment?')) {
                  deleteInstallment(id);
                }
                event.stopPropagation();
              }}
            />
          );
        }
      },
    });

const createColumnsInstallments = (deleteInstallment, assetTapeView = ASSET_TAPE_VIEW.NEW) => {
  if (assetTapeView === ASSET_TAPE_VIEW.OLD) {
    return [
      ...sharedInstallmentMetaColumns(),
      columnHelper.accessor('purchasePrice', {
        cell: (info) => getCurrencyCellValue(info),
        header: 'Purchase Price',
      }),
      columnHelper.accessor('totalSold', {
        cell: (info) => getCurrencyCellValue(info),
        header: 'Amount Sold',
      }),
      columnHelper.accessor('amountDueOutstanding', {
        cell: (info) => getCurrencyCellValue(info),
        header: 'Amount Due Outstanding',
      }),
      columnHelper.accessor('principalDueOutstanding', {
        cell: (info) => getCurrencyCellValue(info),
        header: 'Principal Due Outstanding',
      }),
      columnHelper.accessor('feeDueOutstanding', {
        cell: (info) => getCurrencyCellValue(info),
        header: 'Fee Due Outstanding',
      }),
      columnHelper.accessor('principalRepaid', {
        cell: (info) => getCurrencyCellValue(info),
        header: 'Principal Repaid',
      }),
      columnHelper.accessor('feeRepaid', {
        cell: (info) => getCurrencyCellValue(info),
        header: 'Fee Repaid',
      }),
      columnHelper.accessor('lastRepaymentAtMillis', {
        cell: (info) =>
          info.getValue() === null ? (
            'N/A'
          ) : (
            <Text whiteSpace={'nowrap'}>
              {moment(info.getValue()).format('MMM DD, YYYY')}
            </Text>
          ),
        header: 'Last Repayment Date',
      }),
      columnHelper.accessor('createdAtMillis', {
        cell: (info) =>
          info.getValue() === null ? (
            'N/A'
          ) : (
            <Text whiteSpace={'nowrap'}>
              {moment(info.getValue()).format('MMM DD, YYYY')}
            </Text>
          ),
        header: 'Date',
      }),
      installmentActionsColumn(deleteInstallment),
    ];
  }

  return [
    ...sharedInstallmentMetaColumns(),
    columnHelper.accessor('totalGrowthInvestment', {
      cell: (info) => getCurrencyCellValue(info),
      header: 'Growth Investment',
    }),
    columnHelper.accessor('currentReturns', {
      cell: (info) => getCurrencyCellValue(info),
      header: 'Current Returns',
    }),
    columnHelper.accessor('totalExpectedReturns', {
      cell: (info) => getCurrencyCellValue(info),
      header: 'Total Expected Returns',
    }),
    columnHelper.accessor('currentExposure', {
      cell: (info) => getCurrencyCellValue(info),
      header: 'Current Exposure',
    }),
    columnHelper.accessor('cumulativeFee', {
      cell: (info) => getCurrencyCellValue(info),
      header: 'Cumulative Fee',
    }),
    columnHelper.accessor('expectedFutureFee', {
      cell: (info) => getCurrencyCellValue(info),
      header: 'Expected Future Fee',
    }),
    columnHelper.accessor('lastRepaymentAtMillis', {
      cell: (info) =>
        info.getValue() === null ? (
          'N/A'
        ) : (
          <Text whiteSpace={'nowrap'}>
            {moment(info.getValue()).format('MMM DD, YYYY')}
          </Text>
        ),
      header: 'Last Repayment Date',
    }),
    columnHelper.accessor('createdAtMillis', {
      cell: (info) =>
        info.getValue() === null ? (
          'N/A'
        ) : (
          <Text whiteSpace={'nowrap'}>
            {moment(info.getValue()).format('MMM DD, YYYY')}
          </Text>
        ),
      header: 'Installment Creation',
    }),
    installmentActionsColumn(deleteInstallment),
  ];
};

function getBankAccountCellValue(info, bankAccountsById) {
    const bankAccountDisplayName = formatTwelveBankAccountLabelById(
        bankAccountsById,
        info.getValue()
    );
    if (!bankAccountDisplayName) {
        return 'N/A';
    }
    return <Text>{bankAccountDisplayName}</Text>;
}

const createColumnsRepayments = (bankAccountsById, facilityFees, invoices, navigate) => {
    const columns = [
        columnHelper.accessor('paid_at', {
            cell: (info) =>
                info.getValue() === null ? (
                    'N/A'
                ) : (
                    <Text whiteSpace={'nowrap'}>
                        {moment(info.getValue()).format('MMM DD, YYYY')}
                    </Text>
                ),
            header: 'Repayment Date',
        }),

        columnHelper.accessor('created_at', {
            cell: (info) =>
                info.getValue() === null ? (
                    'N/A'
                ) : (
                    <Text whiteSpace={'nowrap'}>
                        {moment(info.getValue()).format('MMM DD, YYYY')}
                    </Text>
                ),
            header: 'Created At',
        }),

        columnHelper.accessor('amount', {
            // Includes any facility fee paid alongside this repayment, so the
            // top-level amount reflects the whole wire, not just installments.
            // A fee-only repayment has a null installment amount — that must not
            // short-circuit past adding the fee, or the row shows N/A despite the
            // customer having actually paid the fee amount.
            cell: (info) => {
                const installmentAmount = info.getValue();
                const feeAmount = paidFacilityFeeAmountForRepayment(facilityFees, info.row.original);
                if ((installmentAmount === null || installmentAmount === undefined) && feeAmount === 0) {
                    return 'N/A';
                }
                return <Currency value={(installmentAmount || 0) + feeAmount} maximumFractionDigits={2} />;
            },
            header: 'Amount',
        }),

        columnHelper.accessor('set_off_amount', {
            cell: (info) => getCurrencyCellValueNullAsZero(info),
            header: 'Set Off Amount',
        }),

        columnHelper.accessor('principal', {
            cell: (info) => getCurrencyCellValue(info),
            header: 'Principal',
        }),

        // TODO: interest??
        columnHelper.accessor('interest', {
            cell: (info) => getCurrencyCellValueNullAsZero(info),
            header: 'Interest',
        }),

        columnHelper.accessor('bank_account_id', {
            cell: (info) => getBankAccountCellValue(info, bankAccountsById),
            header: 'Bank Account',
        }),

        columnHelper.display({
            id: 'invoice',
            header: 'Invoice',
            cell: (info) => {
                const invoice = invoiceForRepayment(invoices, info.row.original);
                if (!invoice) {
                    return '—';
                }
                return (
                    <Button
                        size="xs"
                        variant="link"
                        onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/accounting-internal/${invoice.id}`);
                        }}
                    >
                        {invoice.invoiceNumber}
                    </Button>
                );
            },
        }),
    ];

    return columns;
};

const defaultSortingInstallments = [
  { id: 'installmentType', desc: false },
  { id: 'createdAtMillis', desc: true }
];

const defaultSortingRepayments = [
    { id: 'paid_at', desc: true },
    { id: 'created_at', desc: true }
];

const OperationsCustomerOverview = () => {
  const [loading, setLoading] = useState(true);
  const [loadingAmortization, setLoadingAmortization] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [amortizationData, setAmortizationData] = useState(null);
  const [facilityFees, setFacilityFees] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [revenueRecognition, setRevenueRecognition] = useState(
    REVENUE_RECOGNITION_TYPE.INTEREST_FIRST
  );
  const [assetTapeView, setAssetTapeView] = useState(ASSET_TAPE_VIEW.NEW);
  const [isAmortizationPredicted, setIsAmortizationPredicted] = useState(true);
  const [
    showAmortizationPreviewInstallments,
    setShowAmortizationPreviewInstallments,
  ] = useState(true);

  const [overviewColumnsInstallments, setOverviewColumnsInstallments] = useState([]);
  const [overviewColumnsRepayments, setOverviewColumnsRepayments] = useState([]);

  const [repaymentsCsvData, setRepaymentsCsvData] = useState([]);

  const [bankAccountsById, setBankAccountsById] = useState({});

  const toast = useToast();
  const navigate = useNavigate();

  const { customer, isCustomerLoading } = useContext(CockpitCustomerContext);

  const customerId = customer?.id;

  useEffect(() => {
    const initializeData = async () => {
      if (customer) {
        const initStart = performance.now();
        console.log('[OperationsCustomerOverview] initializeData start', { customerId: customer?.id });

        let t0 = performance.now();
        const bankAccountsById = await fetchBankAccounts();
        console.log('[OperationsCustomerOverview] fetchBankAccounts took', Math.round(performance.now() - t0), 'ms');

        setBankAccountsById(bankAccountsById);

        t0 = performance.now();
        await getAmortizationData();
        console.log('[OperationsCustomerOverview] getAmortizationData took', Math.round(performance.now() - t0), 'ms');

        try {
          const fees = await getFacilityFeesApi(customer.id);
          setFacilityFees(fees || []);
        } catch {
          // non-critical; table still renders without facility fees
        }

        try {
          const invoicesData = await listAccountingInvoicesApi({ customerId: customer.id });
          setInvoices(invoicesData || []);
        } catch {
          // non-critical; repayments table still renders without invoice links
        }

        setOverviewColumnsInstallments(createColumnsInstallments(deleteInstallment, ASSET_TAPE_VIEW.NEW));

        console.log('[OperationsCustomerOverview] initializeData total took', Math.round(performance.now() - initStart), 'ms');
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (customer) {
      (async () => {
        const t0 = performance.now();
        console.log('[OperationsCustomerOverview] loadCustomerInstallmentsOverviewData start', { customerId });
        await loadCustomerInstallmentsOverviewData();
        console.log('[OperationsCustomerOverview] loadCustomerInstallmentsOverviewData took', Math.round(performance.now() - t0), 'ms');
      })();
    }
  }, [revenueRecognition]);

  useEffect(() => {
    setOverviewColumnsInstallments(createColumnsInstallments(deleteInstallment, assetTapeView));
  }, [assetTapeView]);

  useEffect(() => {
    setOverviewColumnsRepayments(createColumnsRepayments(bankAccountsById, facilityFees, invoices, navigate));
  }, [bankAccountsById, facilityFees, invoices]);

  const generateRepaymentsCsvData = (event, done) => {
    let csvData = [];
    let csvDataHeader = [];
    csvDataHeader.push('#')
    csvDataHeader.push('Repayment:Repayment Date')
    csvDataHeader.push('Repayment:Created At')
    csvDataHeader.push('Repayment:Amount')
    csvDataHeader.push('Repayment:Set Off Amount')
    csvDataHeader.push('Repayment:Principal')
    csvDataHeader.push('Repayment:Interest')
    csvDataHeader.push('Repayment:Bank Account')
    csvDataHeader.push('Repayment:Invoice')

    csvDataHeader.push('Installment:Installment Date')
    csvDataHeader.push('Installment:Created At')
    csvDataHeader.push('Installment:Purchase Price')
    csvDataHeader.push('Installment:Cohort Dates')
    csvDataHeader.push('Installment:Owner')
    csvDataHeader.push('Installment:Repayment Amount')
    csvDataHeader.push('Installment:Principal')
    csvDataHeader.push('Installment:Interest')

    csvData.push(csvDataHeader);

    const sortedRepayments = overviewData?.repayments?.sort((a, b) => {
      const paidAtDiff = new Date(b.paid_at) - new Date(a.paid_at);
      if (paidAtDiff !== 0) return paidAtDiff;
      return b.id - a.id;
    })

    let i = 1
    sortedRepayments.forEach((row, index) => {
      const repaymentColumns = () => {
        const cols = []
        cols.push(`${moment(row.paid_at).format('MMM DD, YYYY')}`)
        cols.push(`${moment(row.created_at).format('MMM DD, YYYY')}`)
        cols.push(`${Currency({value: row.amount + paidFacilityFeeAmountForRepayment(facilityFees, row)})}`)
        cols.push(`${Currency({value: row.set_off_amount ?? 0})}`)
        cols.push(`${Currency({value: row.principal})}`)
        cols.push(`${Currency({value: row.interest ?? 0})}`)
        cols.push(formatTwelveBankAccountLabelById(bankAccountsById, row.bank_account_id) || '---')
        cols.push(`${invoiceForRepayment(invoices, row)?.invoiceNumber || '---'}`)
        return cols
      }

      // A fee-only repayment has no installment allocations to iterate — it
      // still needs a row, just with the installment columns left blank.
      if (!row.repayment_allocations || row.repayment_allocations.length === 0) {
        const csvRow = [`${i++}`, ...repaymentColumns(), '---', '---', '---', '---', '---', '---', '---', '---']
        csvData.push(csvRow);
        return;
      }

      row.repayment_allocations.forEach((row2, index2) => {
        let csvRow = []
        csvRow.push(`${i++}`)
        csvRow.push(...repaymentColumns())

        csvRow.push(`${moment(row2.installment.info.transferDate).format('MMM DD, YYYY')}`)
        csvRow.push(`${moment(row2.installment.created_at).format('MMM DD, YYYY')}`)
        csvRow.push(`${Currency({value: row2.installment.info.purchasePrices[0].amount})}`)
        csvRow.push(`${row2.installment.info.cohortStartDate ? moment(row2.installment.info.cohortStartDate).format('MMM DD, YYYY') : '---'} - ${row2.installment.info.cohortEndDate ? moment(row2.installment.info.cohortEndDate).format('MMM DD, YYYY') : '---'}`)
        csvRow.push(`${row2.installment.info.owner}`)
        csvRow.push(`${Currency({value: row2.amount})}`)
        csvRow.push(`${Currency({value: row2.principal??0})}`)
        csvRow.push(`${Currency({value: row2.interest??0})}`)

        csvData.push(csvRow);
      });
    });
    setRepaymentsCsvData(csvData);
    done(true);
  };


  const fetchBankAccounts = async () => {
        try {
            // setLoading(true);
            const t0 = performance.now();
            const bankAccounts = await getTwelveBankAccountsApi();
            console.log('[OperationsCustomerOverview] getTwelveBankAccountsApi took', Math.round(performance.now() - t0), 'ms');
            const bankAccountsById = bankAccounts.reduce((acc, bankAccount) => {
                acc[bankAccount.id] = bankAccount;
                return acc;
            }, {});

            // console.log('Bank Accounts By Id:', bankAccountsById);

            return bankAccountsById;
        } catch (error) {
            console.error(error);
        } finally {
            // setLoading(false);
        }
    };

    const loadCustomerInstallmentsOverviewData = async () => {
    setLoading(true);
    try {
      const t0 = performance.now();
      const _data = await getCustomerInstallmentsOverviewApi({
        customerId,
        revenueRecognition,
      });
      console.log('[OperationsCustomerOverview] getCustomerInstallmentsOverviewApi took', Math.round(performance.now() - t0), 'ms');

      // console.log('Customer Installments Overview Data:', _data);

      setOverviewData(_data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch installments data',
        status: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getAmortizationData = async ({ refreshData = false } = {}) => {
    try {
      setLoadingAmortization(true);
      const _data = await getAmortizationApi({ customerId, refreshData });
      setAmortizationData(_data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch amortization data',
        status: 'error',
      });
      throw error;
    } finally {
      setLoadingAmortization(false);
    }
  };

  const deleteInstallment = async (installmentId) => {
    try {
      setLoading(true);
      await deleteInstallmentApi({ customerId, installmentId });
      await loadCustomerInstallmentsOverviewData();
      await getAmortizationData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete installment',
        status: 'error',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

    const deleteRepayment = async (repaymentId) => {
        try {
            setLoading(true);
            await deleteRepaymentApi({ repaymentId });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to delete repayment',
                status: 'error',
            });
            throw error;
        } finally {
            setLoading(false);
        }
    };

  if (loading) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  const downloadMonthlyStatement = async () => {
    // The statement is measured at the end of the month preceding today.
    const statementDate = moment().subtract(1, 'month').endOf('month');

    try {
      await downloadCustomerMonthlyStatementApi({
        customerId,
        fileName: `Treisar_Monthly_Statement_${customer.companyName}_(${statementDate.format(
          'YYYY.MM.DD'
        )}).xlsx`,
        downloadDate: moment().format('YYYY-MM-DD'),
      });
    } catch (error) {
      toast({
        title: 'Error',
        description:
          (await readBlobErrorMessage(error)) || 'Failed to download monthly statement',
        status: 'error',
      });
      throw error;
    }
  };

  // console.log('overviewData', overviewData);

    const deleteLast = async () => {
      const repayment = overviewData?.repayments?.find(repayment => repayment.is_last);
      if (!repayment) {
        alert('No last repayment found to delete');
        return;
      }
      if (window.confirm(`Are you sure you want to delete this repayment?\n(Repayment Date: ${moment(repayment.paid_at).format('MMM DD, YYYY')}, Created At: ${moment(repayment.created_at).format('MMM DD, YYYY')}, Amount: ${Currency({value: repayment.amount})}`)) {
        // console.log('deleteLast', overviewData?.repayments, repayment.id);
        await deleteRepayment(repayment.id);
        await loadCustomerInstallmentsOverviewData();
        await getAmortizationData();
      }
    }

    const nestedRowDataSelector = (dataRow) => {
      const rows = [...(dataRow.repayment_allocations || [])];
      paidFacilityFeesForRepayment(facilityFees, dataRow).forEach((fee) => {
        rows.push({ type: FACILITY_FEE_NESTED_ROW, fee });
      });
      return rows;
    };

    const nestedRowRenderer = (dataRow) =>
      dataRow.type === FACILITY_FEE_NESTED_ROW
        ? renderFacilityFeeNestedRow(dataRow)
        : renderInstallmentAllocationRow(dataRow);

    return (
    <Box maxW={'container.xl'} mt={-10}>
      <OverlaySpinnerWrapper show={loading || isCustomerLoading}>
        <BackLink text={'Back to customer list'} path={'/operations'} />
        <Box mt={100}>
          <Flex mb={4} justify={'end'} align={'center'} gap={4}>
            <RevenueRecognitionSelect
              value={revenueRecognition}
              onChange={setRevenueRecognition}
            />
            <ButtonGroup size="sm" isAttached variant="outline">
              <Button
                onClick={() => setAssetTapeView(ASSET_TAPE_VIEW.OLD)}
                colorScheme={assetTapeView === ASSET_TAPE_VIEW.OLD ? 'brand' : undefined}
                variant={assetTapeView === ASSET_TAPE_VIEW.OLD ? 'solid' : 'outline'}
              >
                Old view
              </Button>
              <Button
                onClick={() => setAssetTapeView(ASSET_TAPE_VIEW.NEW)}
                colorScheme={assetTapeView === ASSET_TAPE_VIEW.NEW ? 'brand' : undefined}
                variant={assetTapeView === ASSET_TAPE_VIEW.NEW ? 'solid' : 'outline'}
              >
                New view
              </Button>
            </ButtonGroup>
          </Flex>
          <Flex justifyContent={'space-between'} align={'start'}>
            <OperationsKpis
              view={assetTapeView}
              {...overviewData.kpis}
              totalFacilityFee={facilityFees.reduce((s, f) => s + (f.amount || 0), 0)}
              repaidFacilityFee={facilityFees.filter((f) => f.status === 'paid').reduce((s, f) => s + (f.amount || 0), 0)}
            />
          </Flex>

          <Flex justify={'end'}>
            <Button
              variant={'outline'}
              leftIcon={<DownloadIcon />}
              onClick={downloadMonthlyStatement}
            >
              Download Monthly Statement
            </Button>
          </Flex>
          <Card
            mt={'40px'}
            w={loadingAmortization ? 'full' : 'max'}
            maxW={'full'}
            minH={loadingAmortization ? 400 : 'auto'}
            display={
              !loadingAmortization &&
              amortizationData?.installments?.length === 0
                ? 'none'
                : 'block'
            }
          >
            {loadingAmortization && (
              <AbsoluteCenter>
                {' '}
                <Spinner />
              </AbsoluteCenter>
            )}
            {amortizationData?.installments?.length > 0 && (
              <>
                <Flex justify={'space-between'} align={'center'}>
                  <Flex gap={2} align={'baseline'}>
                    <Heading size="md">Amortization Table</Heading>
                    {amortizationData?.lastUpdateMillis && (
                      <Text>
                        (Last Revenue Update:{' '}
                        {moment(amortizationData?.lastUpdateMillis).format(
                          'MMM DD, YYYY HH:mm'
                        )}
                        )
                      </Text>
                    )}
                  </Flex>
                  <Flex gap={5} align={'center'}>
                    <Flex gap={1} align={'center'}>
                      <FormLabel m={0} htmlFor="isChecked">
                        Show Preview Installments
                      </FormLabel>
                      <Switch
                        id="showAmortizationPreviewInstallments"
                        isChecked={showAmortizationPreviewInstallments}
                        onChange={(event) =>
                          setShowAmortizationPreviewInstallments(
                            event.target.checked
                          )
                        }
                        size={'xl'}
                      />
                    </Flex>
                    <Flex gap={1} align={'center'}>
                      <FormLabel m={0} htmlFor="isChecked">
                        Is Predicted
                      </FormLabel>
                      <Switch
                        id="isPredicted"
                        isChecked={isAmortizationPredicted}
                        onChange={(event) =>
                          setIsAmortizationPredicted(event.target.checked)
                        }
                        size={'xl'}
                      />
                    </Flex>
                    <Button
                      variant={'outline'}
                      leftIcon={<Icon as={FaSync} />}
                      onClick={() => getAmortizationData({ refreshData: true })}
                    >
                      Refresh Revenue Data
                    </Button>
                  </Flex>
                </Flex>
                <Box mt={5} mx={-5}>
                  {!loadingAmortization && (
                    <AmortizationTable
                      data={amortizationData.installments}
                      isPredicted={isAmortizationPredicted}
                      showPreviewInstallments={
                        showAmortizationPreviewInstallments
                      }
                      facilityFees={facilityFees}
                    />
                  )}
                </Box>
              </>
            )}
          </Card>
          <Card mt={'40px'}>
            <Flex justify={'end'}>
              <Flex>
                <Stack direction="row" spacing={4} align="center">
                  <Button
                    w={220}
                    variant={'outline'}
                    onClick={() => {
                      navigate(`/operations/${customerId}/installment/new`);
                    }}
                  >
                    CREATE INSTALLMENT
                  </Button>
                  <Button
                    w={220}
                    variant={'brand'}
                    onClick={() => {
                      navigate(`/operations/${customerId}/repayment`);
                    }}
                  >
                    REPAYMENT
                  </Button>
                </Stack>
              </Flex>
            </Flex>
            <Box minH={300} mx={-5} mt={10}>
              <Heading size="md">Installments</Heading>
              {overviewData?.installments?.length > 0 && overviewColumnsInstallments?.length > 0 && (
                <DataTable
                  columns={overviewColumnsInstallments}
                  data={overviewData.installments}
                  hasPagination={true}
                  defaultSorting={defaultSortingInstallments}
                  onRowClick={(row) => {
                    navigate(`/operations/${customerId}/installment/${row.id}`);
                  }}
                  rowHeight={'100px'}
                />
              )}
            </Box>
          <Box minH={300} mx={-5} mt={10}>

              <Flex justify={'space-between'} align={'center'}>
                  <Flex gap={2} align={'baseline'}>
                      <Heading size="md">Installment Repayments</Heading>
                  </Flex>
                  <Flex gap={5} align={'center'}>
                      <Flex gap={1} align={'center'}>
                          <Button variant={'outline'} leftIcon={<DownloadIcon />}>
                              <CSVLink
                                  data={repaymentsCsvData}
                                  filename="installment-repayments.csv"
                                  onClick={generateRepaymentsCsvData}
                                  asyncOnClick={true}
                              >
                                  Download CSV
                              </CSVLink>
                          </Button>
                      </Flex>
                      <Button
                          variant={'outline'}
                          leftIcon={<Icon as={FaTrash} />}
                          onClick={() => deleteLast()}
                      >
                          Delete Last
                      </Button>
                  </Flex>
              </Flex>
              {overviewData?.installments?.length > 0 && (
                  <>
                      <CollapsableDataTable
                          columns={overviewColumnsRepayments}
                          data={overviewData.repayments}
                          hasPagination={true}
                          defaultSorting={defaultSortingRepayments}
                          // onRowClick={(row) => {
                          //     navigate(`/operations/${customerId}/installment/${row.id}`);
                          // }}
                          onNestedRowClick={(row) => {
                              // console.log("onNestedRowClick", row);
                              if (row.type === FACILITY_FEE_NESTED_ROW) return;
                              navigate(`/operations/${customerId}/installment/${row.installment.id}`);
                          }}
                          nestedRowDataSelector={nestedRowDataSelector}
                          nestedRowRenderer={nestedRowRenderer}
                          rowHeight={'100px'}
                          customerId={customerId}
                      />
                  </>
              )}
          </Box>
          </Card>
        </Box>
      </OverlaySpinnerWrapper>
    </Box>
  );
};

OperationsCustomerOverview.propTypes = {};

export default OperationsCustomerOverview;
